import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { AddEditUldModalComponent } from 'src/app/components/position-operations/outbound-scan-gle/add-edit-uld-modal/add-edit-uld-modal.component';
import { CloseUldModalComponent } from 'src/app/components/position-operations/outbound-scan-gle/close-uld-modal/close-uld-modal.component';
import { OutboundScanGleModalComponent } from 'src/app/components/position-operations/outbound-scan-gle/outbound-scan-gle-modal/outbound-scan-gle-modal.component';
import { TransferUldModalComponent } from 'src/app/components/position-operations/outbound-scan-gle/transfer-uld-modal/transfer-uld-modal.component';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, map } from 'rxjs';
import { ModalFormValue } from 'src/app/core/models/common/data.model';
import { Items } from 'src/app/core/models/common/items.model';
import { TableColumn } from 'src/app/core/models/common/table-column.model';
import {
  CloseULORequest,
  GetOutboundGLEResponse,
  OpenULORequest,
  OutboundScan,
} from 'src/app/core/models/position-operations/outbound-gle.model';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { OutboundGLEService } from 'src/app/core/services/position-operations/outbound-gle/outbound-gle.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { expandCollapse } from '../../animations/animations';
import { IMAGE_SRC, NO_RESULTS } from '../../shared-constants';
import {
  ColumnIds,
  ColumnText,
  CONST,
  EQUIPMENT_TYPE_OPTIONS,
  EXCEL_FILE_NAME,
  SORT_OPTIONS,
  SORTS_TYPES,
  TYPE_OPTIONS,
} from './outbound-scan-table.constant';

@Component({
  selector: 'app-outbound-scan-table',
  templateUrl: './outbound-scan-table.component.html',
  styleUrls: ['./outbound-scan-table.component.scss'],
  animations: [expandCollapse],
  standalone: false,
})
export class OutboundScanTableComponent implements OnInit {
  destroy = inject(DestroyRef);
  private userService = inject(UserService);

  private readonly _outboundGLEService = inject(OutboundGLEService);
  private readonly _dialog = inject(MatDialog);

  readonly SRC = IMAGE_SRC;

  readonly displayedColumns = Object.values(ColumnIds);
  readonly COLUMNTEXT = ColumnText;
  readonly NO_RESULTS = NO_RESULTS;
  readonly equipmentTypeOption = EQUIPMENT_TYPE_OPTIONS;
  sortOption = SORT_OPTIONS;
  readonly typeOption = TYPE_OPTIONS;
  readonly CONST = { ...CONST };

  @Output() transferUld = new EventEmitter();

  dataSource = new MatTableDataSource<OutboundScan>();
  draftRequestBody: any;
  // Create column search objects
  createAdminTableColumn(columnId: string) {
    return {
      itemId: columnId,
      ids: [],
      formControl: new FormControl(''),
    };
  }

  // Create admin table columns
  equipmentNumberData = this.createAdminTableColumn(ColumnIds.EQUIPMENT_NUMBER);
  equipmentTypeData = this.createAdminTableColumn(ColumnIds.EQUIPMENT_TYPE);
  sealNumberData = this.createAdminTableColumn(ColumnIds.SEAL_NUMBER);
  createSortData = this.createAdminTableColumn(ColumnIds.CREATE_SORT_DATE);
  sortData = this.createAdminTableColumn(ColumnIds.SORT);
  loadNameData = this.createAdminTableColumn(ColumnIds.LOAD_NAME);
  loadChartData = this.createAdminTableColumn(ColumnIds.LOAD_CHART);
  movementData = this.createAdminTableColumn(ColumnIds.MOVEMENT);
  parentUldData = this.createAdminTableColumn(ColumnIds.PARENT_ULD);
  typeData = this.createAdminTableColumn(ColumnIds.TYPE);

  resetSelectDropdowns = new BehaviorSubject<boolean>(false);

  sortDate!: Date | null;
  dropdownOpen = [false, false, false, false];
  maxDate: Date = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() + 4,
  );
  minDate: Date = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() - 1,
  );
  constructor(
    private readonly dialog: MatDialog,
    public commonUtils: CommonUtils,
    private readonly outboundGleService: OutboundGLEService,
  ) {}

  /**
   * Setting up filter for seach fields
   *
   * @memberof OutboundScanTableComponent
   */
  ngOnInit(): void {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map(() => {
          this.resetState();
          this.getOpenULOs();
          this.dataSource.filterPredicate = this.createFilter();
        }),
      )
      .subscribe();
  }
  resetState() {
    this.dataSource.data = [];
    this.sortDate = null;
    this.clearAllFilters();
  }

  getOpenULOs() {
    this._outboundGLEService
      .getOpenULOs()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((data: GetOutboundGLEResponse) => {
          this.dataSource.data = this.mapToDatasource(data);
        }),
      )
      .subscribe();
  }

  mapToDatasource(data: GetOutboundGLEResponse): OutboundScan[] {
    const tableData: OutboundScan[] = [];
    data?.response?.ulos.forEach((dat) => {
      const origin = dat.loadKey.origin;
      const isQualifierIncluded = origin.sortCode?.length == 2;
      let sort: string;
      if (dat.loadKey.discriminator == 'INTERNATIONAL') {
        if (isQualifierIncluded) {
          sort = `${origin.sortCode}`;
        } else {
          sort = `${origin.sortCode}${origin.qualifier || ''}`;
        }
      } else {
        sort = origin.sortCode;
      }

      const outboundData: OutboundScan = {
        id: dat.id,
        createSortDate: this.commonUtils
          .getDateFromString(dat.sortDate)
          .toLocaleString(),
        equipmentNumber: dat.loadKey.uld.number,
        equipmentType: dat.loadKey.uld.type,
        sealNumber: dat.loadKey.uld.seal,
        sort,
        loadName: dat.loadKey.loadName,
        loadChart: dat.loadKey.loadChart,
        movement: dat.loadKey.movement.mvmtNum,
        parentUld: dat.parentUld,
        type: dat.loadKey.loadType,
        positionBay: dat.loadKey.position.bay,
        positionBelt: dat.loadKey.position.belt,
        uldCount: dat.uldCount,
        losseCount: dat.losseCount,
        rawUlo: dat,
      };

      tableData.push(outboundData);
    });
    return tableData;
  }

  /**
   *
   * Create filter for table
   * @private
   * @return {*}  {(data: any, filter: string) => boolean}
   * @memberof OutboundScanTableComponent
   */
  private createFilter(): (data: OutboundScan, filter: string) => boolean {
    return (data: OutboundScan, filter: string) => {
      const filters = JSON.parse(filter);

      return Object.keys(filters).every((filterKey) => {
        //Allows opening of row if filters are applied already
        if (filters[filterKey] === '') {
          return true;
        }
        return this.filterByColumn(filterKey, data, filters);
      });
    };
  }

  /**
   * Filter column by column id
   *
   * @private
   * @param {string} filterKey
   * @param {*} data
   * @param {*} filters
   * @return {*}
   * @memberof OutboundScanTableComponent
   */
  private filterByColumn(
    filterKey: string,
    data: OutboundScan,
    filters: OutboundScan,
  ) {
    switch (filterKey) {
      case ColumnIds.CREATE_SORT_DATE:
        if (filters.createSortDate !== '') {
          const filteredStartDate = new Date(filters.createSortDate);
          const dataDate = new Date(data.createSortDate);
          if (dataDate.getTime() === filteredStartDate.getTime()) {
            return true;
          }
        }
        return false;
      case ColumnIds.EQUIPMENT_TYPE:
      case ColumnIds.SORT:
      case ColumnIds.TYPE: {
        const value = filters[filterKey];
        const rowValue = data[filterKey];
        const searchData = rowValue?.toString().toLowerCase() || '';
        const searchValue = value?.toString().toLowerCase();
        if (searchValue) {
          return searchValue.includes(searchData);
        }
        return searchData.includes(searchValue);
      }
      default:
        return this.setDefaults(filters, data, filterKey);
    }
  }

  /**
   * return default values
   *
   * @private
   * @param {string} filterKey
   * @param {*} data
   * @param {*} filters
   * @return {*}
   * @memberof OutboundScanTableComponent
   */
  private setDefaults(
    filters: OutboundScan,
    data: OutboundScan,
    filterKey: string,
  ) {
    const value = filters[filterKey as keyof OutboundScan];
    const rowValue = data[filterKey as keyof OutboundScan];
    const searchData = rowValue?.toString().toLowerCase() ?? '';
    const searchValue = value?.toString().toLowerCase();
    return searchData.includes(searchValue ?? '');
  }

  /**
   * Triggers when a dropdown option is checked/unchecked
   *
   * @param {*} $event
   * @param {AdminTableColumn} field
   * @memberof OutboundScanTableComponent
   */
  onStatusChangeForDropdowns($event: string | string[], field: TableColumn) {
    field.ids[0] = $event as unknown as Items;
    switch (field.itemId) {
      case ColumnIds.EQUIPMENT_TYPE:
        this.equipmentTypeData.formControl.setValue($event as string);
        break;
      case ColumnIds.SORT:
        this.sortData.formControl.setValue($event as string);
        break;
      case ColumnIds.TYPE:
        this.typeData.formControl.setValue($event as string);
        break;
      default:
        break;
    }
    this.search();
  }

  /**
   * Filtering through the search bar
   *
   * @memberof OutboundScanTableComponent
   */
  search() {
    this.commonUtils.searchFilter(
      [
        this.equipmentNumberData,
        this.sealNumberData,
        this.createSortData,
        this.loadNameData,
        this.loadChartData,
        this.movementData,
        this.parentUldData,
      ],
      this.dataSource,
      [this.equipmentTypeData, this.sortData, this.typeData],
    );
  }

  /**
   * Clear all the filters if user has searched/filtered any column
   *
   * @memberof OutboundScanTableComponent
   */
  clearAllFilters() {
    this.commonUtils.clearAllFilters(this.dataSource, this.dataSource.data, [
      this.equipmentNumberData.formControl,
      this.equipmentTypeData.formControl,
      this.sealNumberData.formControl,
      this.createSortData.formControl,
      this.sortData.formControl,
      this.loadNameData.formControl,
      this.loadChartData.formControl,
      this.movementData.formControl,
      this.parentUldData.formControl,
      this.typeData.formControl,
    ]);
    this.resetSelectDropdowns.next(true);
  }

  /**
   * Figure out if filter button is enabled
   * Should not be enabled if user has not filtered any column
   *
   * @readonly
   * @memberof OutboundScanTableComponent
   */
  get isFilterEnabled() {
    return (
      this.equipmentNumberData.formControl.value !== '' ||
      this.equipmentTypeData.formControl.value !== '' ||
      this.sealNumberData.formControl.value !== '' ||
      this.createSortData.formControl.value !== '' ||
      this.sortData.formControl.value !== '' ||
      this.loadNameData.formControl.value !== '' ||
      this.loadChartData.formControl.value !== '' ||
      this.movementData.formControl.value !== '' ||
      this.parentUldData.formControl.value !== '' ||
      this.typeData.formControl.value !== ''
    );
  }

  /**
   * Returns Sort for given accronyms
   *
   * @param {OutboundScan} outboundScan
   * @return {*}  {string}
   * @memberof OutboundScanTableComponent
   */
  getSort(outboundScan: OutboundScan): string {
    switch (outboundScan.sort) {
      case SORTS_TYPES.SUNRISE:
        return 'S';
      case SORTS_TYPES.MORNING:
        return 'M';
      case SORTS_TYPES.DAY:
        return 'D';
      case SORTS_TYPES.TWILIGHT:
        return 'T';
      case SORTS_TYPES.NIGHT:
        return 'N';
      case SORTS_TYPES.PRELOAD:
        return 'P';
      case SORTS_TYPES.LOCAL:
        return 'L';
      case SORTS_TYPES.AFTERNOON:
        return 'A';
      default:
        return '';
    }
  }

  /**
   * Export table data in excel file format
   *
   * @memberof OutboundScanTableComponent
   */
  exportExcel(): void {
    const filteredData = this.dataSource.filteredData.map((dat) => {
      return {
        equipmentNumber: dat.equipmentNumber,
        equipmentType: dat.equipmentType,
        sealNumber: dat.sealNumber,
        createSortDate: dat.createSortDate,
        sort: dat.sort,
        loadName: dat.loadName,
        loadChart: dat.loadChart,
        movement: dat.movement,
        parentUld: dat.parentUld,
        type: dat.type,
      };
    });

    this.commonUtils.exportExcel(
      Object.values(ColumnText),
      EXCEL_FILE_NAME,
      filteredData,
    );
  }

  /**
   * This method is use to open TransferUldModal.
   * on close it will insert new row top of table
   *
   * @memberof OutboundScanTableComponent
   */
  openTransferUldModal(ulo: any) {
    this.dialog
      .open(TransferUldModalComponent, {
        width: '34.25rem',
        disableClose: true,
        data: {
          ulo,
        },
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp?.transfer && resp?.res?.code === 200) {
          this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
          this.getOpenULOs();
        }
      });
  }

  /**
   * used to add transfer row top of the table
   *
   * @param {*} transfer
   * @memberof OutboundScanTableComponent
   */
  addTransferRow(transfer: OutboundScan) {
    const newRow = {
      ...transfer,
      sealNumber: '',
      createSortDate: '',
      sort: '',
      loadName: '',
      loadChart: '',
      movement: '',
      parentUld: '',
      type: '',
    };
    const tableData = this.dataSource.data;
    tableData.unshift(newRow);
    this.dataSource.data = [...tableData];
  }

  /**
   * This method is use to open AddEditUldModal.
   * on close it will insert new row top of table
   *
   * @memberof OutboundScanTableComponent
   */
  addNewUldModal() {
    const sort =
      (this.sortData?.formControl as FormControl).value[0] ||
      SORT_OPTIONS[0].id;
    this.dialog
      .open(AddEditUldModalComponent, {
        width: '34.25rem',
        disableClose: true,
        autoFocus: false,
        data: {
          formData: {
            sortDate: this.sortDate,
            sort: sort === 'n' ? null : sort,
          },
          currentStep: 1,
        },
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp?.newUldRow) {
          this.addNewUldRow(resp.newUldRow, resp.newUldRequest);
        }
      });
  }

  /**
   * used to add new ULD row top of the table
   *
   * @param {*} uldData
   * @memberof OutboundScanTableComponent
   */
  addNewUldRow(uldData: ModalFormValue, newUldRequest?: OpenULORequest) {
    const newRow = {
      ...uldData,
      sealNumber: newUldRequest?.loadKey.uld.seal,
      createSortDate: newUldRequest?.sortDate,
      loadName: newUldRequest?.loadKey.loadName,
      loadChart: newUldRequest?.loadKey.loadChart,
      parentUld: '',
      type: newUldRequest?.loadKey.loadType,
      rawUlo: newUldRequest,
    };
    const tableData = this.dataSource.data;
    tableData.unshift(newRow);
    this.dataSource.data = [...tableData];

    this.draftRequestBody = newUldRequest;

    this.openOutboundPopup(newRow, true);
  }

  openOutboundPopup(equipment: OutboundScan, dialogForNewUlo: boolean) {
    this._dialog
      .open(OutboundScanGleModalComponent, {
        width: '35.75rem',
        panelClass: 'dialog-offset-viewport',
        disableClose: true,
        data: {
          equipment: equipment,
          draftRequestBody: this.draftRequestBody,
          outboundScan: true,
          dialogForNewUlo: dialogForNewUlo,
        },
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp) {
          this._outboundGLEService
            .getOpenULOs()
            .pipe(
              takeUntilDestroyed(this.destroy),
              map((data: GetOutboundGLEResponse) => {
                this.dataSource.data = this.mapToDatasource(data);
              }),
            )
            .subscribe();
        }
      });
  }

  /**
   * This method is use to open AddEditUldModal.
   *
   * @memberof OutboundScanTableComponent
   */
  editUldModal(outboundScan: OutboundScan, index: number) {
    this.dialog
      .open(AddEditUldModalComponent, {
        width: '34.25rem',
        disableClose: true,
        autoFocus: false,
        data: {
          isEditMode: true,
          formData: {
            ...outboundScan,
            load: outboundScan.loadName,
            sortDate: new Date(outboundScan.createSortDate),
          },
          currentStep: 3,
          tableData: outboundScan,
          index,
        },
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp?.editUld) {
          this.setEditedDataToTable(resp, outboundScan, index);
        }
      });
  }

  /**
   * Use set edited data to table
   *
   * @param {*} data
   * @param {*} rowData
   * @param {number} index
   * @memberof OutboundScanTableComponent
   */
  setEditedDataToTable(
    data: ModalFormValue,
    rowData: OutboundScan,
    index: number,
  ) {
    const editUld = data?.editUld;
    const editedData = {
      ...rowData,
      ...editUld,
      sealNumber: editUld?.sealControl ?? rowData?.sealNumber,
      createSortDate: editUld?.sortDate,
      loadName: editUld?.load,
    };
    const tableData = this.dataSource.data;
    tableData[index] = editedData;
    this.dataSource.data = [...tableData];
  }

  /**
   * Used to open close uld modal
   * submit it will delete selected record from table
   *
   * @param {OutboundScan} outboundScan
   * @param {number} index
   * @memberof OutboundScanTableComponent
   */
  closeUld(outboundScan: OutboundScan, index: number) {
    const fallbackDate = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }).format(new Date());

    // Adding it to debug if necessary
    if (!outboundScan?.createSortDate) {
      console.warn(
        'Missing createSortDate in outboundScan load:',
        outboundScan,
      );
    }

    const safeOutboundScan = {
      ...outboundScan,
      createSortDate: outboundScan.createSortDate || fallbackDate,
    };

    this.dialog
      .open(CloseUldModalComponent, {
        width: '34.25rem',
        disableClose: true,
        autoFocus: false,
        data: {
          rowData: safeOutboundScan,
          index,
        },
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp?.delete) {
          this.deleteUld(resp.index, outboundScan, resp.percentage);
        }
      });
  }

  /**
   * used to delete record from table
   *
   * @param {number} _index
   * @memberof OutboundScanTableComponent
   */
  deleteUld(_index: number, outboundScan: OutboundScan, percentage: string) {
    const request: CloseULORequest = {
      percentFull: Number(percentage),
      uloId: outboundScan.id,
    };
    this.outboundGleService.closeUlo(request).subscribe((data) => {
      if (data.code === 200) {
        this.getOpenULOs();
        this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
      }
    });
  }
}
